/** Thrown by `loadEnv` for either a missing `window.__ENV__` or a schema violation. */
export class RuntimeConfigError extends Error {
    constructor(message) {
        super(`runtime-config: ${message}`);
        this.name = "RuntimeConfigError";
    }
}
function currentGlobalEnv() {
    return globalThis.__ENV__;
}
/**
 * Validates and returns the typed runtime config for `schema`.
 *
 * Reads `window.__ENV__` by default (the file `/__env.js` defines at
 * container start — see generate-env-js.mjs) and fails fast: a missing
 * `window.__ENV__`, a missing required key, or a failed `validate` throws
 * immediately rather than letting the app boot with a blank value that
 * silently breaks SSO or a role check. This mirrors the "sentinel leaked ->
 * refuse to start" guarantee `docker-runtime-env.mjs` gives the Next build.
 *
 * Pass `source` explicitly in tests instead of mutating the global.
 */
export function loadEnv(schema, source = currentGlobalEnv()) {
    if (!source) {
        throw new RuntimeConfigError("window.__ENV__ missing — /__env.js did not load before the app bundle");
    }
    const errors = [];
    const result = {};
    for (const spec of schema.keys) {
        const raw = source[spec.key];
        const value = raw?.trim() ? raw : undefined;
        if (spec.required) {
            if (!value) {
                errors.push(`missing required key "${spec.key}"`);
                continue;
            }
            const validationError = spec.validate?.(value);
            if (validationError) {
                errors.push(`"${spec.key}" ${validationError}`);
                continue;
            }
            result[spec.key] = value;
        }
        else {
            result[spec.key] = value;
        }
    }
    if (errors.length > 0) {
        throw new RuntimeConfigError(`invalid runtime env — ${errors.join("; ")}`);
    }
    return Object.freeze(result);
}
//# sourceMappingURL=load-env.js.map